import React, { useEffect } from 'react';
import { reduxForm, InjectedFormProps, Field } from 'redux-form';
import { useTranslation } from 'react-i18next';

import * as Alert from '../../../../common/services/alert';
import {
  Row,
  Group,
  Label,
  TextInput,
  LanguageInput,
  CheckboxInput,
  OrganisationCard
} from '../../../../common/components/form';
import Confirm from '@common/components/confirm-button';

import type { Organisation } from '@common/types/objects';
import { EBootDestinationType, EComponentTypes } from '@common/definitions';
import { useFormValues } from '@common/hooks';
import { useEnabledComponents } from '@common/hooks/use-enabled-components';

import { PrivacyFieldSelect } from '../../../core/components/privacy-select';
import { updateOrganisation } from '../../actions';
import { CompanyValues } from '../../components/company-values';
import validate from '../validators/organisation';
import i18n from '../../../../i18n';

const onSubmitFn = async (values: any, dispatch: any) => {
  try {
    const payload = {
      ...values,
      ...(values.boot_destination !== undefined
        ? {
          boot_destination:
            values.boot_destination === true
              ? EBootDestinationType.SOCIAL
              : EBootDestinationType.HOME,
        }
        : {}),
    };
    await dispatch(updateOrganisation(payload));

    Alert.success(i18n.t('organisation:forms_organisation_success'));
  } catch (response: any) {
    Alert.forStatus(response?.status_code, {
      warning: i18n.t('organisation:forms_organisation_warning'),
      error: i18n.t('organisation:forms_organisation_error'),
    });
  }
};

const formConnector = reduxForm<FormValues, OwnProps>({
  form: 'organisation',
  validate,
  onSubmit: onSubmitFn,
});

type FormValues = {
  name: string;
  default_language_locale: string;
  access_request_links_enabled: boolean;
  boot_destination: boolean;
  show_organisation_users: boolean;
  settings: {
    default_user_privacy: any;
    require_user_introduction: boolean;
  };
};

type OwnProps = {
  organisation: Organisation;
};

type Props = InjectedFormProps<FormValues, OwnProps> & OwnProps;

const UnconnectedOrganisationForm = ({
  organisation,
  initialize,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const data = useFormValues(props.form);
  const { homeEnabled } = useEnabledComponents({
    homeEnabled: EComponentTypes.HOME,
  });

  useEffect(() => {
    initialize({
      name: organisation.name,
      default_language_locale: organisation.language.locale,
      access_request_links_enabled: organisation.access_request_links_enabled,
      show_organisation_users: organisation?.show_organisation_users || false,
      ...(homeEnabled
        ? { boot_destination: organisation?.boot_destination === EBootDestinationType.SOCIAL }
        : {}),
      settings: {
        default_user_privacy: organisation.settings.default_user_privacy,
        require_user_introduction: organisation.settings.require_user_introduction,
      },
    });
  }, [organisation]);

  const renderBootDestination = () => (
    <CheckboxInput
      name="boot_destination"
      label={t('organisation:boot_destination')}
      description={t('organisation:boot_destination_description')}
      onChange={(value) => {
        if (value === true) return;
        props.change('boot_destination', value);
      }}
    />
  );

  return (
    <form onSubmit={props.handleSubmit}>
      <OrganisationCard
        title={t('organisation:card_organization_setup_title')}
        description={t('organisation:card_organization_setup_description')}
      >
        <Row>
          <Group>
            <Label text={t('organisation:forms_organisation_name')} />
            <TextInput name="name" />
          </Group>
        </Row>
        <Row>
          <Group>
            <Label
              text={t('organisation:forms_organisation_language')}
              description={t('organisation:forms_organisation_language_description')}
            />
            <Field
              name="default_language_locale"
              component={LanguageInput}
              width="100%"
              translations={false}
            />
          </Group>
        </Row>
      </OrganisationCard>

      <OrganisationCard
        title={t('organisation:card_employee_preferences_title')}
        description={t('organisation:card_employee_preferences_description')}
      >
        <Row>
          <Group>
            <Label text={t('organisation:privacy_settings_phone_number')} />
            <PrivacyFieldSelect
              name="settings.default_user_privacy.show_phone_num"
              props={{ onlyBoolean: true, width: '100%' }}
            />
          </Group>
        </Row>

        <Row>
          <Group>
            <Label text={t('organisation:privacy_settings_date_of_birth')} />
            <PrivacyFieldSelect
              name="settings.default_user_privacy.show_date_of_birth"
              props={{ width: '100%' }}
            />
          </Group>
        </Row>
        <Row>
          <Group>
            <Label text={t('organisation:privacy_settings_allow_invites')} />
            <PrivacyFieldSelect
              name="settings.default_user_privacy.allow_chat_invites"
              props={{ width: '100%' }}
            />
          </Group>
        </Row>

        <Row>
          <Group>
            <Label text={t('organisation:privacy_settings_last_active')} />
            <PrivacyFieldSelect
              name="settings.default_user_privacy.show_last_active"
              props={{ width: '100%' }}
            />
          </Group>
        </Row>
      </OrganisationCard>

      <OrganisationCard
        contentStyle={{ maxWidth: '500px' }}
        title={t('organisation:card_app_visibility_title')}
        description={t('organisation:card_app_visibility_description')}
      >
        <Row>
          <Group>
            <CheckboxInput
              name="access_request_links_enabled"
              description={t('organisation:forms_organisation_invite_links_qr_code_description')}
              label={t('organisation:forms_organisation_access_invitations_label')}
            />
          </Group>
        </Row>

        <Row>
          <Group>
            <CheckboxInput
              name="settings.require_user_introduction"
              description={t('organisation:ask_employee_introduction_description')}
              label={t('organisation:new_employee_onboarding_label')}
            />
          </Group>
        </Row>

        {homeEnabled && (
          <Row>
            <Group>
              {data.boot_destination === false ? (
                <Confirm
                  title={t('common:overview_content_confirm')}
                  description={t('organisation:boot_destination_confirmation')}
                  onConfirm={() => {
                    props.change('boot_destination', true);
                  }}
                >
                  <div>{renderBootDestination()}</div>
                </Confirm>
              ) : (
                renderBootDestination()
              )}
            </Group>
          </Row>
        )}

        <Row>
          <Group>
            <CheckboxInput
              name="show_organisation_users"
              description={t('organisation:forms_organisation_show_all_users_description')}
              label={t('organisation:employee_&_community_visibility_label')}
            />
          </Group>
        </Row>
      </OrganisationCard>

      <OrganisationCard
        title={t('organisation:card_company_values_title')}
        description={t('organisation:card_company_values_description')}
      >
        <CompanyValues />
      </OrganisationCard>
    </form>
  );
};

export const OrganisationForm = formConnector(UnconnectedOrganisationForm);
